import { SERVICE_CODES, SF_ONLY_ACCESSIBLE_SERVICES } from '@localstack/constants';
import { useHasOnlySnowflakeAccess, useDisableFeatures, useRoutes } from '@localstack/services';
import { AwsServiceTile } from '@localstack/ui';
import { ReactElement, useState } from 'react';

import { Box, Grid, List, ListItem, TextField, Typography } from '@mui/material';

import { RESOURCES_TYPES, RESOURCE_GROUPS, ResourceGroups } from '@localstack/resources';

import { ResourceBrowserViewProps } from '../props';

const DEFAULT_COLUMN_WIDTH = 240;
const PAGE_TITLE = 'Resource Browser';

export const ResourcesOverview = ({
  Layout,
  clientOverrides,
  useAuthProvider,
  postOnClick,
  onFeatureUpgradeButtonClick,
  isFreemiumHobbyCommercialMisuse,
}: ResourceBrowserViewProps): ReactElement => {
  const { goto } = useRoutes();
  const [query, setQuery] = useState('');
  const { hasFeature } = useAuthProvider();
  const hasOnlySnowflakeTrial = useHasOnlySnowflakeAccess({ useAuthProvider });
  const enableAction = useDisableFeatures({ endpoint: clientOverrides?.endpoint });

  const filteredGroups = RESOURCE_GROUPS.reduce((groupsMemo, groups) => {
    const subgroups = groups.reduce((groupMemo, group) => {
      const filteredCodes = group[1].filter(
        (code) => code.toLowerCase().includes(query) || SERVICE_CODES[code]?.toLowerCase()?.includes(query),
      );

      const accessibleCodes = filteredCodes.filter(
        (code) => !hasOnlySnowflakeTrial || SF_ONLY_ACCESSIBLE_SERVICES.includes(code),
      );
      return accessibleCodes.length ? [...groupMemo, [group[0], accessibleCodes]] : groupMemo;
    }, []);

    return subgroups.length ? [...groupsMemo, [...subgroups]] : groupsMemo;
  }, []) as ResourceGroups;

  const navigateHandler = (code: keyof typeof RESOURCES_TYPES) => {
    postOnClick?.({ serviceCode: code });
    goto(RESOURCES_TYPES[code]);
  };

  return (
    <Layout title={PAGE_TITLE}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box mb={4}>
            <TextField
              fullWidth
              autoFocus
              onChange={(e) => setQuery(e.target.value?.toLocaleLowerCase())}
              variant="outlined"
              size="small"
              label="Which service are you looking for?"
            />
          </Box>
          <Box display="flex" flexWrap="wrap" justifyContent="space-between" mb={4}>
            {filteredGroups.map((group) => (
              <Box mr={2} width={DEFAULT_COLUMN_WIDTH} key={group.join('')}>
                {group.map(([title, services]) => (
                  <Box key={title}>
                    <Typography variant="caption">{title}</Typography>
                    <List dense disablePadding>
                      {services.map((code) => (
                        <ListItem key={code} disableGutters>
                          <AwsServiceTile
                            code={code}
                            size="large"
                            title={SERVICE_CODES[code]}
                            hideTooltip
                            clickable={enableAction}
                            navigateTo={() => navigateHandler(code)}
                            hasFeature={hasFeature}
                            urlOpenerFunc={onFeatureUpgradeButtonClick}
                            isFreemiumHobbyCommercialMisuse={isFreemiumHobbyCommercialMisuse}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Layout>
  );
};
